import { useState } from 'react'
import { Alert, Box, Button, Collapse, IconButton, Snackbar, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import type { UUID } from 'crypto'
import usePermissions from '@/utils/hooks/users'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { useRequestUpgrade } from '@/utils/hooks/subscriptions'
import { isFreeTier } from '@/utils/utils'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import SubscribeDialog from './SubscribeDialog'

const DISMISS_KEY = 'onboardingSubscribeDismissed'

const getDismissed = (): boolean => {
  try { return sessionStorage.getItem(DISMISS_KEY) === 'true' } catch { return false }
}

export default function OnboardingSubscribeBanner() {
  const { orgUuid } = useOnboardingV2()
  const [wizardData] = useWizardData()
  const { user, token, orgs, hasAgreedToTerms } = getCurrentUser()
  const { permissions, isLoading: permissionsLoading } = usePermissions()
  const { orgData, isLoading: orgsLoading } = useCurrentUserOrgs(
    token as string, user, orgs as UUID[],
  )
  const currentOrg = orgData?.find(
    (org) => org.uuid === orgUuid,
  ) || null

  const [dismissed, setDismissed] = useState(getDismissed)
  const [dialogOpen, setDialogOpen] = useState(false)
  const upgradeMutation = useRequestUpgrade()

  // Org creator is always admin; default to admin if permissions haven't loaded
  const isAdmin = permissionsLoading || !permissions || permissions.role === 'admin'

  // Don't show until org exists, T&C accepted, and org is on free tier
  if (!orgUuid || !hasAgreedToTerms) return null
  if (!orgsLoading && currentOrg && !isFreeTier(currentOrg)) return null
  // Only show after the subscribe prompt has been dismissed
  if (!wizardData.subscribePromptDismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    try { sessionStorage.setItem(DISMISS_KEY, 'true') } catch { /* noop */ }
  }

  const handleRequestUpgrade = () => {
    if (!orgUuid || !token) return
    upgradeMutation.mutate({ orgUuid: orgUuid as UUID, token })
  }

  return (
    <>
      <Collapse in={!dismissed}>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pb: 1 }}>
          <Alert
            severity="info"
            role="note"
            aria-label="Subscription upgrade prompt"
            sx={{
              width: '100%',
              maxWidth: 960,
              alignItems: 'center',
              borderRadius: '12px',
              '& .MuiAlert-message': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              },
            }}
            action={
              <IconButton
                onClick={handleDismiss}
                size="small"
                sx={{ color: 'inherit' }}
                aria-label="dismiss upgrade prompt"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              You're on the Free Plan. Upgrade to a Paid plan to unlock
              unlimited matches, applications, and more.
            </Typography>
            {isAdmin ? (
              <Button
                variant="contained"
                size="small"
                disableRipple
                onClick={() => setDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', width: '150px' }}
              >
                Upgrade Account
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                disableRipple
                onClick={handleRequestUpgrade}
                disabled={upgradeMutation.isPending || upgradeMutation.isSuccess}
                sx={{
                  whiteSpace: 'wrap',
                  textTransform: 'none',
                  width: '100%',
                  maxWidth: '300px',
                }}
              >
                {upgradeMutation.isSuccess
                  ? 'Request Sent'
                  : 'Send account admin a request to upgrade'}
              </Button>
            )}
          </Alert>
        </Box>
      </Collapse>
      <SubscribeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onLater={() => setDialogOpen(false)}
        skipIntro
      />
      <Snackbar
        open={upgradeMutation.isError}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          upgradeMutation.reset()
        }}
        message="Failed to send upgrade request"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        aria-label="Upgrade request error notification"
      />
    </>
  )
}
